<template>
  <div class="student-page-wrapper">
    <!-- Confetti Animation -->
    <div class="confetti-container">
      <div v-for="i in 50" :key="i" class="confetti" :style="getConfettiStyle(i)"></div>
    </div>

    <!-- Header -->
    <StudentHeader />
    <!-- Main Confirmation Card -->

    <div class="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-44 animate-slide-up">
      <!-- Success Icon with Sparkle Effect -->
      <div class="flex justify-center mb-6">
        <div class="relative animate-scale-in" style="animation-delay: 0.2s">
          <!-- Sparkle decoration -->
          <div class="absolute -top-3 -left-5 animate-sparkle">
            <svg
              class="w-8 h-8 drop-shadow-[2px_2px_0px_rgba(0,0,0,1)]"
              viewBox="0 0 24 24"
            >
              <path
                d="M12 0l2.598 7.402L22 10l-7.402 2.598L12 20l-2.598-7.402L2 10l7.402-2.598L12 0z"
                fill="#fbbf24"
                stroke="black"
                stroke-width="1"
              />
            </svg>
          </div>

          <!-- Main success circle with gradient -->
          <div
            class="w-20 h-20 bg-gradient-to-br from-emerald-400 via-green-500 to-teal-600 border-3 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)] rounded-full flex items-center justify-center transform hover:scale-105 transition-transform duration-200"
          >
            <svg class="w-12 h-12 text-white animate-check-draw" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <!-- Secondary sparkle -->
          <div class="absolute -bottom-4 -right-4 animate-sparkle" style="animation-delay: 0.3s">
            <svg
              class="w-6 h-6 drop-shadow-[2px_2px_0px_rgba(0,0,0,1)]"
              viewBox="0 0 24 24"
            >
              <path
                d="M12 0l2.598 7.402L22 10l-7.402 2.598L12 20l-2.598-7.402L2 10l7.402-2.598L12 0z"
                fill="#fcd34d"
                stroke="black"
                stroke-width="1"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Confirmation Message -->
      <div
        class="bg-white rounded-2xl border-3 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)] px-8 pb-8 pt-12 text-center"
      >
        <h1 class="text-2xl font-bold text-gray-900 mb-2">Appointment Scheduled Successfully!</h1>
        <p class="text-gray-600 text-sm px-3 mb-8">
          You will receive an email when your tutor approves or declines the appointment.
        </p>

        <!-- Session Details - 2 Column Grid -->
        <div class="grid grid-cols-2 gap-x-6 gap-y-6 text-left">
          <!-- Booking Date & Time -->
          <div>
            <h3 class="text-sm font-normal text-gray-500 mb-1">Date & Time</h3>
            <div class="flex items-start gap-2">
              <CalendarIcon class="w-5 h-5 text-gray-600 inline-block flex-shrink-0 mt-0.5" />
              <p class="text-md font-semibold text-gray-900 leading-snug">
                {{ formatBookingDateTime() }}
              </p>
            </div>
          </div>

          <!-- Service Location/Mode -->
          <div v-if="sessionDetails.location || sessionDetails.mode">
            <h3 class="text-sm font-normal text-gray-500 mb-1">
              {{ sessionDetails.mode === "online" ? "Mode" : "Location" }}
            </h3>
            <div class="flex items-start gap-2">
              <VideoCameraIcon
                v-if="sessionDetails.mode === 'online'"
                class="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5"
              />
              <MapPinIcon v-else class="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
              <p class="text-md font-semibold text-gray-900 leading-snug">
                {{
                  sessionDetails.mode === "online"
                    ? "Online Session"
                    : sessionDetails.location || "In-person"
                }}
              </p>
            </div>
          </div>

          <!-- Subject -->
          <div v-if="sessionDetails.subject">
            <h3 class="text-sm font-normal text-gray-500 mb-1">Subject</h3>
            <div class="flex items-start gap-2">
              <BookOpenIcon class="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
              <p class="text-md font-semibold text-gray-900 leading-snug">
                {{ sessionDetails.subject }}
              </p>
            </div>
          </div>

          <!-- Tutor -->
          <div v-if="displayTutorName">
            <h3 class="text-sm font-normal text-gray-500 mb-1">Tutor</h3>
            <div class="flex items-start gap-2">
              <UserIcon class="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
              <p class="text-md font-semibold text-gray-900 leading-snug">
                {{ displayTutorName }}
              </p>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-14 space-y-3">
          <!-- View Sessions Button -->
          <button
            @click="goToMySessions"
            class="w-full view-btn bg-gradient-to-r from-emerald-500 via-green-600 to-teal-600 hover:from-emerald-600 hover:via-green-700 hover:to-teal-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer shadow-[3px_3px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:translate-y-[-2px] border-2 border-black"
          >
            <CalendarIcon class="w-5 h-5" />
            View My Sessions
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import StudentHeader from "@/components/StudentHeader.vue";
import { db } from "@/firebase";
import { doc, getDoc } from "firebase/firestore";
import { logError } from "@/utils/secureLogger";

import { useRouter, useRoute } from "vue-router";
import {
  CalendarIcon,
  BookOpenIcon,
  UserIcon,
  MapPinIcon,
  VideoCameraIcon,
} from "@heroicons/vue/24/outline";

const router = useRouter();
const route = useRoute();

// Session details from route params or query
const sessionDetails = ref({
  dateTime: "",
  endTime: "",
  subject: "",
  mode: "",
  location: "",
  tutorName: "",
  tutorId: "",
});

// Loading state for tutor data
const isLoadingTutor = ref(false);

// Function to fetch tutor details from Firestore
const fetchTutorDetails = async (tutorId: string) => {
  if (!tutorId) return null;

  try {
    isLoadingTutor.value = true;
    const tutorDoc = await getDoc(doc(db, "users", tutorId));

    if (tutorDoc.exists()) {
      const tutorData = tutorDoc.data();
      return `${tutorData.firstName || ""} ${tutorData.lastName || ""}`.trim();
    }
    return null;
  } catch (error) {
    logError("Error fetching tutor details", error);
    return null;
  } finally {
    isLoadingTutor.value = false;
  }
};

onMounted(async () => {
  // Try to get session details from sessionStorage first (persists across refresh)
  const storedData = sessionStorage.getItem("bookingConfirmationData");

  let sessionData;
  if (storedData) {
    // If we have stored data, use it
    try {
      sessionData = JSON.parse(storedData);
      // DON'T clear sessionStorage yet - keep it in case of page refresh
      // It will be cleared when user navigates away to another page
    } catch (error) {
      logError("Error parsing stored booking data", error);
      // Fall back to query parameters
      sessionData = route.query;
    }
  } else {
    // Fall back to route query parameters
    sessionData = route.query;
  }

  sessionDetails.value = {
    dateTime: (sessionData.dateTime as string) || "",
    endTime: (sessionData.endTime as string) || "",
    subject: (sessionData.subject as string) || "",
    mode: (sessionData.mode as string) || "",
    location: (sessionData.location as string) || "",
    tutorName: (sessionData.tutorName as string) || "",
    tutorId: (sessionData.tutorId as string) || "",
  };

  // If tutorName is not provided but tutorId exists, fetch tutor details
  if (!sessionDetails.value.tutorName && sessionDetails.value.tutorId) {
    const fetchedTutorName = await fetchTutorDetails(sessionDetails.value.tutorId);
    if (fetchedTutorName) {
      sessionDetails.value.tutorName = fetchedTutorName;
    }
  }
});

// Computed property for tutor name with fallback
const displayTutorName = computed(() => {
  if (isLoadingTutor.value) {
    return "Loading...";
  }

  const tutorName = sessionDetails.value.tutorName;
  if (tutorName && tutorName.trim() !== "") {
    return tutorName;
  }

  // Show fallback if no tutor name is available
  return sessionDetails.value.tutorId ? "Your Tutor" : null;
});

const formatBookingDateTime = (): string => {
  if (!sessionDetails.value.dateTime) return "Not specified";

  try {
    // Parse the ISO string - this should be the correct local time
    const bookingDate = new Date(sessionDetails.value.dateTime);

    // Check if the date is valid
    if (isNaN(bookingDate.getTime())) {
      return "Invalid date";
    }

    // Format the date part (shorter format - no weekday, no year)
    const dateStr = bookingDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    // Format start time
    const startTimeStr = bookingDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return `${dateStr}, ${startTimeStr}`;
  } catch (error) {
    logError("Error formatting booking date", error);
    return sessionDetails.value.dateTime;
  }
};

const goToMySessions = () => {
  // Clear sessionStorage when navigating away
  sessionStorage.removeItem("bookingConfirmationData");
  router.push({ name: "student-sessions" });
};

const bookAnotherSession = () => {
  // Clear sessionStorage when navigating away
  sessionStorage.removeItem("bookingConfirmationData");
  router.push({ name: "find-tutors" });
};

const goHome = () => {
  // Clear sessionStorage when navigating away
  sessionStorage.removeItem("bookingConfirmationData");
  router.push({ name: "find-tutors" });
};

// Confetti animation function
const getConfettiStyle = (index: number) => {
  const colors = [
    "#ff6b6b",
    "#4ecdc4",
    "#45b7d1",
    "#f9ca24",
    "#f0932b",
    "#eb4d4b",
    "#6c5ce7",
    "#a29bfe",
  ];
  const delay = Math.random() * 2; // Random delay up to 2s
  const duration = 3 + Math.random() * 2; // Random duration 3-5s
  const startX = Math.random() * 100; // Random start position
  const endX = startX + (Math.random() * 60 - 30); // Random end position
  const rotation = Math.random() * 720; // Random rotation

  return {
    backgroundColor: colors[index % colors.length],
    animationDelay: `${delay}s`,
    animationDuration: `${duration}s`,
    left: `${startX}%`,
    "--end-x": `${endX}%`,
    "--rotation": `${rotation}deg`,
  };
};
</script>

<style scoped>
.student-page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url("/student-reg-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
  width: 100%;
  overflow: hidden;
}

/* Custom animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out;
}

/* Slide up animation - smooth entrance from bottom */
@keyframes slideUp {
  0% {
    opacity: 0;
    transform: translateY(60px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-up {
  animation: slideUp 1s cubic-bezier(0.34, 1.56, 0.64, 1), float 3s ease-in-out 1s infinite;
}

/* Floating animation - soft up and down motion */
@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Scale in animation for icon */
@keyframes scaleIn {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-scale-in {
  animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Sparkle animation */
@keyframes sparkle {
  0%, 100% {
    opacity: 0.6;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.3);
  }
}

.animate-sparkle {
  animation: sparkle 1.5s ease-in-out infinite;
}

/* Check mark draw animation */
@keyframes checkDraw {
  0% {
    stroke-dasharray: 0, 100;
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dasharray: 100, 0;
    stroke-dashoffset: 0;
  }
}

.animate-check-draw {
  stroke-dasharray: 100;
  animation: checkDraw 0.6s ease-out forwards;
  animation-delay: 0.4s;
}

.view-btn {
  background: #155dfc;
  color: white;
  border: 2px solid #0034a5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 2px 2px 0px #0b2c8d;
  transform: translateY(0px);
  transition: all 0.15s ease;
}

.view-btn:hover {
  background: #0041d0;
  transform: translateY(-1px);
  box-shadow: 3px 3px 0px #0b2c8d;
}

.view-btn:active {
  transform: translateY(1px);
  box-shadow: 1px 1px 0px #0b2c8d;
}

/* Confetti Animation */
.confetti-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1000;
}

.confetti {
  position: absolute;
  width: 10px;
  height: 10px;
  top: -10px;
  border-radius: 50%;
  animation: confetti-fall var(--duration, 4s) ease-out var(--delay, 0s) 1 forwards;
}

@keyframes confetti-fall {
  0% {
    transform: translateY(-100vh) translateX(0) rotateZ(0deg);
    opacity: 1;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) translateX(var(--end-x, 0%)) rotateZ(var(--rotation, 0deg));
    opacity: 0;
  }
}

/* Different confetti shapes */
.confetti:nth-child(4n + 1) {
  border-radius: 0;
  width: 8px;
  height: 8px;
}

.confetti:nth-child(4n + 2) {
  border-radius: 0;
  width: 12px;
  height: 4px;
}

.confetti:nth-child(4n + 3) {
  width: 6px;
  height: 6px;
}

.confetti:nth-child(4n + 4) {
  border-radius: 0;
  width: 4px;
  height: 12px;
}
</style>
